import { buildVenuePath } from '../utils/venueRouting';
import React, { useState, useContext, useEffect } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import AppContext from '../contexts/AppContext';
import { useNavigate } from 'react-router-dom';
import { getLuminance } from '../utils/ColorLuminance.js';
import { openDiscover } from '../utils/discover';

const searchContent = (content, searchTerm, isHotelOnly = false) => {
  const candidates = new Set(); // 用于存储所有可能的候选节点

  const collectCandidates = (node) => {
    if (!node) return;

    // 检查过滤哪个部分的content
    if (isHotelOnly) {
      if (node.searchTags === "AreaManagedEndpoint") {
        return;
      }
    } else {
      if (node.searchTags === "HotelManagedEndpoint") {
        return;
      }
    }

    // 将当前节点添加到候选集
    if (node.isLeaf)
      candidates.add(node);

    // 递归处理子节点
    if (node.attributes && node.attributes.length > 0) {
      node.attributes.forEach((child) => collectCandidates(child));
    }
  };

  // 收集所有候选节点
  collectCandidates(content);

  // 筛选符合条件的结果
  const results = Array.from(candidates).filter((item) => {
    const tags = item.searchTags?.toLowerCase() || '';
    const isMatchingTags = tags.split(';').some((tag) => tag.trim() === searchTerm.toLowerCase()); // 精确匹配
    const isNameMatch = item.name.toLowerCase().includes(searchTerm.toLowerCase()); // 名称模糊匹配

    return isNameMatch || isMatchingTags;
  });

  return results;
};


const SearchTabs = ({ isHotelOnly = false }) => {
  const { contentInfo, hotelInfo, articleInfo, venueId } = useContext(AppContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [articleResults, setArticleResults] = useState([]);
  const [isOverlayVisible, setOverlayVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (searchTerm.length >= 2) {
      const results = searchContent(contentInfo, searchTerm, isHotelOnly);
      setSearchResults(results);
      setOverlayVisible(true);

      // 文章搜索逻辑
      const tmpArticleResults = [];
      articleInfo.forEach((item) => {
        if (item.name.toLowerCase().includes(searchTerm.toLowerCase())) {
          tmpArticleResults.push(item);
        }
      });
      setArticleResults(tmpArticleResults);
      document.body.style.overflow = 'hidden';
    } else {
      setSearchResults([]);
      setArticleResults([]);
      setOverlayVisible(false);
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [searchTerm, contentInfo, articleInfo, isHotelOnly]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  if (!hotelInfo) {
    return <div>Loading...</div>;
  }
  const themeColor = hotelInfo.theme?.primaryColor || hotelInfo.theme?.color || 'rgb(0,0,0)';
  const textColor = hotelInfo.theme?.secondaryColor || 'rgb(255,255,255)';
  const designedColor = getLuminance(themeColor) > 0.7 ? textColor : themeColor;
  const fontColor = hotelInfo.theme?.fontColor || 'rgb(0,0,0)';

  return (
    <div className="relative">
      {/* 搜索栏 */}
      <div className=" flex z-30 relative" style={{ boxShadow: '0 0.25rem 0.25rem rgba(0, 0, 0, 0.1)' }}>
        <div className="flex items-center bg-white px-4 py-2 shadow-sm flex-1">
          <MagnifyingGlassIcon className="h-5 w-5 mr-2" style={{ color: designedColor }} />
          <input
            type="text"
            placeholder="What are you looking for today?"
            value={searchTerm}
            onChange={handleSearchChange}
            className={`flex-grow bg-transparent focus:outline-none text-xs ${searchTerm.length > 0 ? 'text-black' : 'text-gray-600'}`}
          />
        </div>
        {searchTerm.length>0 && <button className="px-4 text-xs w-auto text-white" style={{ backgroundColor: themeColor, boxShadow: '0 0.25rem 0.25rem rgba(0, 0, 0, 0.1)' }} onClick={() => setSearchTerm('')}>Cancel</button>}
      </div>

      {/* 遮罩层 */}
      {isOverlayVisible && (
        <div className="fixed left-0 right-0 top-0 bottom-0 z-10 bg-gray-100 opacity-50" onClick={() => setSearchTerm('')}></div>
      )}

      {/* 搜索结果 */}
      {(searchResults.length > 0 || articleResults.length > 0) && (
        <div className="absolute left-0 right-0 z-20 bg-white shadow-lg p-4 overflow-y-scroll">
          {/* 搜索结果 */}
          {searchResults.length > 0 && (
            <div>
              {searchResults.map((result) => (
                <div
                  key={result.id}
                  className="py-4 cursor-pointer flex flex-row pl-4"
                  onClick={() => openDiscover(hotelInfo?.area?.bitlyUrl)}
                >
                  <MagnifyingGlassIcon className="h-5 w-5 mr-2" style={{ color: themeColor }} />
                  <p className="text-sm">{result.name}</p>
                </div>
              ))}
            </div>
          )}
          {articleResults.length > 0 && (
            <div>
              <p className="text-lg font-semibold px-4 py-2" style={{ color: themeColor }}>Featured Articles</p>
              {articleResults.map((data) => (
                <div className="flex items-center mx-4 my-2 border" onClick={() => navigate(buildVenuePath(`/article/${data.id}`, venueId))}>
                  <div className="w-28 h-28 bg-gray-300 flex-shrink-0">
                    <img src={data.image} alt={data.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="ml-4 flex-grow">
                    <p className="font-semibold text-sm pb-2">{data.name}</p>
                    <p className="text-xs px-2 pb-2" style={{ color: fontColor }}>
                      {data.description.split(' ').slice(0, 15).join(' ')}{data.description.split(' ').length > 15 ? '...' : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchTabs;
